import { useCallback, useEffect, useMemo, useState } from "react";
import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PATHS } from "@/lib/router";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  RefreshCcw,
  BarChart2,
  Target,
  CheckCircle2,
  ChartNoAxesColumn,
} from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { esgService } from "@/features/esg/services";

const StatsTile = ({ title, children }) => (
  <div className="rounded-radius border border-border p-4 space-y-1">
    <p className="text-xs font-medium text-muted-foreground">{title}</p>
    <div className="text-sm font-medium text-foreground">{children}</div>
  </div>
);

const actionButtons = [
  {
    key: "esgFramework",
    icon: FileText,
    path: PATHS.ESG_FRAMEWORKS,
  },
  {
    key: "kpiDashboard",
    icon: BarChart2,
    path: PATHS.ESG_DASHBOARD,
  },
  {
    key: "kpiTargets",
    icon: Target,
    path: PATHS.ESG_TARGETS,
  },
  {
    key: "esgReports",
    icon: CheckCircle2,
    path: PATHS.ESG_REPORTS,
  },
];

const ESGOverviewCard = ({ company, loading, onRefresh }) => {
  const { t } = useTran();
  const navigate = useNavigate();
  const [esgLoading, setEsgLoading] = useState(false);
  const [esgStats, setEsgStats] = useState({
    totalReports: 0,
    completedReports: 0,
    inProgressReports: 0,
    frameworks: [],
    latestUpdate: null,
    targetMetrics: 0,
  });

  const loadEsgStats = useCallback(async () => {
    setEsgLoading(true);
    try {
      const year = new Date().getFullYear();
      const [reportsData, targetsData] = await Promise.all([
        esgService.getReportList({
          page: 1,
          limit: 0,
          sortBy: "updatedAt",
          sortOrder: "desc",
        }),
        esgService.getQuantitativeTargets({ year, page: 1, limit: 1 }),
      ]);

      const reports = reportsData?.reports || [];
      const frameworks = Array.from(
        new Set(reports.map((report) => report?.framework).filter(Boolean)),
      );
      const latestUpdate =
        reports[0]?.updatedAt || reports[0]?.createdAt || null;

      setEsgStats({
        totalReports:
          reportsData?.allCount ??
          reportsData?.pagination?.total ??
          reports.length,
        completedReports:
          reportsData?.completedCount ??
          reports.filter((report) => report?.status === "completed").length,
        inProgressReports:
          reportsData?.inProgressCount ??
          reports.filter((report) => report?.status === "in_progress").length,
        frameworks,
        latestUpdate,
        targetMetrics: targetsData?.pagination?.total ?? 0,
      });
    } catch (error) {
      console.error("Failed to load ESG overview stats:", error);
    } finally {
      setEsgLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEsgStats();
  }, [loadEsgStats]);

  const stats = useMemo(() => {
    const companyUpdate =
      company?.updatedAt || company?.createdAt || company?.created_at || null;
    return {
      ...esgStats,
      latestUpdate: esgStats.latestUpdate || companyUpdate,
    };
  }, [company, esgStats]);

  const {
    totalReports,
    completedReports,
    inProgressReports,
    frameworks,
    latestUpdate,
    targetMetrics,
  } = stats;

  const frameworksLabel = frameworks.length
    ? frameworks.join(", ")
    : t("Not set");

  const reportsSummary = t("companyInformation.reportsSummary", {
    completed: completedReports,
    inProgress: inProgressReports,
  });

  const metricsLabel = t("companyInformation.metricsWithTargets", {
    year: new Date().getFullYear(),
  });

  return (
    <Card className="rounded-radius pt-0">
      <CardHeader className="rounded-t-lg grid gap-2 py-3 bg-gradient-to-br from-slate-100 to-white dark:from-white/10 dark:to-white/5">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base text-primary">
            <ChartNoAxesColumn size={20} />
            {t("companyInformation.esgOverview")}
          </CardTitle>
          <Button
            size="xs"
            variant="ghost"
            className="gap-2 text-secondary hover:text-primary dark:text-primary dark:hover:text-secondary"
            onClick={() => {
              onRefresh?.();
              loadEsgStats();
            }}
            disabled={!company}
          >
            <RefreshCcw />
            {t("Refresh")}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading || esgLoading ? (
          <p className="text-xs text-muted-foreground">
            {t("Syncing latest company details...")}
          </p>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <StatsTile title={t("Reports")}>
            <div className="text-2xl">{totalReports}</div>
            <p className="text-xs text-muted-foreground">{reportsSummary}</p>
          </StatsTile>
          <StatsTile title={t("companyInformation.frameworks")}>
            {frameworksLabel}
          </StatsTile>
          <StatsTile title={metricsLabel}>{targetMetrics}</StatsTile>
          <StatsTile title={t("Latest update")}>
            <p className="text-sm">
              {latestUpdate
                ? formatDateTime(latestUpdate)
                : t("companyInformation.noReports")}
            </p>
          </StatsTile>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          {actionButtons.map(({ key, icon: Icon, path }) => (
            <Button
              key={key}
              size="lg"
              variant="outline"
              className="justify-start gap-2 text-primary"
              onClick={() => navigate(path)}
            >
              {Icon ? <Icon className="h-4 w-4" /> : null}
              {t(`companyInformation.${key}`)}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ESGOverviewCard;
